import React, { forwardRef, useState } from 'react';
import { textVariantStyleMap } from '../../v2BaseComponents/Text';
import {
    TextInput,
    Pressable,
    View,
    ViewStyle,
    StyleProp,
    NativeSyntheticEvent,
    TextInputChangeEventData,
    Platform,
    LayoutChangeEvent,
} from 'react-native';
import { colors } from '../../../utils/theme';
import { getSpacing } from '../../../utils/spacing';
import Icon, { IconName } from '../../v2BaseComponents/Icon';
import HoverDetectionContainer from '../HoverDetectionContainer';
import getBrowser from '../../../utils/getBrowser';

export type MessageInputOwnProps = {
    onSubmit: (message: string) => void;
    isLoading: boolean;
    isDisabled?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    placeholder: string;
    focusOnMount?: boolean;
    disableShadow?: boolean;
    onFocus?: () => void;
    onLayout?: (event: LayoutChangeEvent) => void;
    hideSend?: boolean;
    leftIcon?: IconName;
    submitOnEnter?: boolean;
    /** Stops input at the limit, so a server-side ceiling on the same field is never reached by ordinary typing. */
    maxLength?: number;
};

export type MessageInputProps = MessageInputOwnProps & React.RefAttributes<any>;

const MessageInput: React.FC<MessageInputProps> = forwardRef(
    (
        {
            onSubmit,
            isLoading,
            isDisabled,
            style,
            testID,
            placeholder,
            focusOnMount,
            disableShadow,
            onFocus,
            onLayout,
            hideSend,
            submitOnEnter = true,
            maxLength,
        },
        ref,
    ) => {
        const [hasFocus, setHasFocus] = useState(false);
        const [isButtonHovered, setIsButtonHovered] = useState(false);
        const [message, setMessage] = React.useState('');
        const inputRef = React.useRef<React.LegacyRef<TextInput>>(null);
        const browser = getBrowser(navigator.userAgent);
        const [nativeTextHeight, setNativeTextHeight] = React.useState<undefined | number>(undefined);
        const MAX_HEIGHT = 200;

        React.useEffect(() => {
            if (!isLoading && !isDisabled && focusOnMount) {
                (inputRef.current as any)?.focus();
                onFocus?.();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isLoading, isDisabled, focusOnMount]);

        const resetHeight = () => {
            if (Platform.OS === 'web') {
                if (inputRef.current) (inputRef.current as any).style.height = 'auto';
            } else {
                setNativeTextHeight(undefined);
            }
        };

        const isInputDisabled = isLoading || isDisabled;
        const isSendDisabled = isInputDisabled || !message.trim().length;

        const handleSend = React.useCallback(() => {
            if (!isSendDisabled) {
                onSubmit(message);
                resetHeight();
                setMessage('');
            }
        }, [message, isSendDisabled, onSubmit]);

        const handleChange = React.useCallback(
            (evt: NativeSyntheticEvent<TextInputChangeEventData>) => {
                const el = (evt?.target || evt?.nativeEvent?.target) as any;

                if (!isInputDisabled) {
                    const { text } = evt.nativeEvent;
                    // If user hits enter and submitOnEnter is enabled, send the message
                    if (text.endsWith('\n') && submitOnEnter) {
                        handleSend();
                        return;
                    }
                    setMessage(text);
                }
                if (el && Platform.OS === 'web') {
                    el.style.height = 0;
                    const newHeight = el.offsetHeight - el.clientHeight + el.scrollHeight;
                    el.style.height = `${newHeight}px`;
                }
            },
            [isInputDisabled, handleSend, submitOnEnter],
        );

        const textStyle = textVariantStyleMap[isSendDisabled ? 'placeholder-text' : 'input-text'];

        const getShadowProps = (): StyleProp<ViewStyle> => {
            if (disableShadow) {
                return {};
            } else {
                return {
                    shadowColor: colors.Black,
                    shadowOffset: {
                        width: 0,
                        height: 4,
                    },
                    shadowOpacity: 0.15,
                    shadowRadius: 11,
                };
            }
        };

        return (
            <Pressable
                onPress={() => {
                    onFocus?.();
                    (inputRef.current as any)?.focus?.();
                }}
                style={[style, { width: '100%' }]}
                ref={ref}
                onLayout={onLayout}
            >
                <View
                    style={[
                        {
                            borderRadius: 5,
                            display: 'flex',
                            flexDirection: 'row',
                            paddingVertical: getSpacing(4),
                            paddingHorizontal: getSpacing(6),
                            backgroundColor: colors['White'],
                            borderColor: hasFocus ? colors['Grey'] : colors['Dividers'],
                            borderWidth: 1,
                            ...({ fontSynthesis: browser === 'Safari' ? 'style' : undefined } as any),
                        },
                        getShadowProps(),
                    ]}
                >
                    <TextInput
                        ref={inputRef as any}
                        maxLength={maxLength}
                        style={{
                            height: nativeTextHeight,
                            maxHeight: MAX_HEIGHT,
                            ...textStyle,
                            lineHeight: 26,
                            paddingHorizontal: getSpacing(3),
                            paddingVertical: getSpacing(1),
                            flex: 1,
                            borderColor: 'transparent',
                            ...({ outlineStyle: 'none' } as any),
                            testID: 'prompt-chat-input',
                            ...(Platform.OS === 'web' ? { overflowY: 'auto' } : {}),
                            alignSelf: 'center',
                        }}
                        onFocus={() => {
                            onFocus?.();
                            setHasFocus(true);
                        }}
                        onBlur={() => setHasFocus(false)}
                        value={message}
                        placeholder={placeholder}
                        onChange={handleChange}
                        multiline
                        numberOfLines={1}
                        testID={testID}
                        editable={!isInputDisabled}
                        focusable={!isInputDisabled}
                        onContentSizeChange={(e) => {
                            if (Platform.OS !== 'web') {
                                setNativeTextHeight(e.nativeEvent.contentSize.height);
                            }
                        }}
                    />

                    {hideSend ? null : (
                        <HoverDetectionContainer onHoverChange={setIsButtonHovered}>
                            <Pressable
                                style={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: 36,
                                    height: 36,
                                    borderRadius: 18,
                                    backgroundColor:
                                        colors[
                                            isSendDisabled
                                                ? 'Chat-Input-Button-Inactive'
                                                : isButtonHovered
                                                ? 'Chat-Input-Button-Hover'
                                                : 'Chat-Input-Button-Default'
                                        ],
                                }}
                                disabled={isSendDisabled}
                                onPress={handleSend}
                                testID="send-message-button"
                            >
                                <View style={{ paddingRight: getSpacing(1), paddingTop: getSpacing(1) }}>
                                    <Icon name="Send" color="White" size="chat-button-icon" />
                                </View>
                            </Pressable>
                        </HoverDetectionContainer>
                    )}
                </View>
            </Pressable>
        );
    },
);

export default React.memo(MessageInput);
